import React, { FC, useState, useRef, useEffect } from 'react';
import Pill from '@ingka/pill';
import Badge from '@ingka/badge';
import chevronDown from '@ingka/ssr-icon/paths/chevron-down';
import chevronUp from '@ingka/ssr-icon/paths/chevron-up';
import ListBox from '@ingka/list-box';
import ListView, { ListViewItem } from '@ingka/list-view';
import { IngkaDropdownMenuProps } from './IngkaDropdownMenuProps';
import Search from '@ingka/search';
import { toggleMultiSelect } from './utils';
import { Stack } from '@mui/material';

const IngkaDropdownMenu: FC<IngkaDropdownMenuProps> = ({
  label,
  options,
  selectedValues,
  onToggle,
  id,
  size = 'small',
  showBadge = true,
  limitedHeight,
  addSearch,
  footer,
  defaultToggle,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const handleClickOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const isSelected = selectedValues.length > 0;
  const badgeCount = selectedValues.length;

  return (
    <Stack
      ref={containerRef}
      sx={{
        '.list-box': {
          left: '2px',
        },
      }}
    >
      <ListBox
        popupMode
        open={open}
        style={{
          maxHeight: limitedHeight ? '18rem' : 'none',
          width: '24rem',
        }}
        containerRef={containerRef}
        footer={footer}
        trigger={
          <Pill
            onClick={handleClickOpen}
            size={size}
            trailingIcon={open ? chevronUp : chevronDown}
            selected={isSelected}
            label={
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
                {showBadge && badgeCount > 0 && (
                  <Badge
                    colour="black"
                    label={String(badgeCount)}
                    size="small"
                  />
                )}
              </span>
            }
          />
        }
      >
        <ListView id={id} showDivider size="small">
          {addSearch && (
            <Search
              id={id + 'search'}
              value={searchValue}
              size="small"
              onChange={(e) => setSearchValue(e.target.value)}
              onClear={() => setSearchValue('')}
            />
          )}

          {options
            .filter((option) =>
              option.label.toLowerCase().includes(searchValue.toLowerCase()),
            )
            .map((option) => (
              <ListViewItem
                key={option.value}
                inset
                title={option.label}
                control="checkbox"
                controlProps={{
                  checked: selectedValues.includes(option.value),
                  value: option.value,
                  subtle: false,
                }}
                id={`${id}-${option.value}`}
                onChange={() => {
                  if (defaultToggle) {
                    const newValues = toggleMultiSelect(
                      selectedValues,
                      option.value,
                    );
                    onToggle(newValues);
                    setSearchValue('');
                    return;
                  }
                  setSearchValue('');
                  onToggle(option.value);
                }}
              />
            ))}
        </ListView>
      </ListBox>
    </Stack>
  );
};

export default IngkaDropdownMenu;
